import Fuse from 'fuse.js';
import { SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type ShortcutItem = {
  name: string;
  shortcut: string[];
};

type ShortcutSection = {
  name: string;
  items: ShortcutItem[];
};

const shortcuts: ShortcutSection[] = [
  {
    name: 'General',
    items: [
      { name: 'Go to Pull Requests list', shortcut: ['G', 'P'] },
      { name: 'View keyboard shortcuts', shortcut: ['⌘', '?'] },
    ],
  },
  {
    name: 'PR List',
    items: [
      { name: 'Open search', shortcut: ['/'] },
      { name: 'Go to next item', shortcut: ['J'] },
      { name: 'Go to previous item', shortcut: ['K'] },
    ],
  },
  {
    name: 'PR Detail',
    items: [
      // { name: 'Go to Discussions', shortcut: ['G', 'D'] },
      { name: 'Go to Info tab', shortcut: ['G', 'I'] },
      { name: 'Go to Files tab', shortcut: ['G', 'F'] },
      { name: 'Go to Comments tab', shortcut: ['G', 'C'] },
      { name: 'Go to Activity tab', shortcut: ['G', 'A'] },
      // { name: 'Add comment', shortcut: ['C'] },
      { name: 'Approve', shortcut: ['L', 'G', 'T', 'M'] },
    ],
  },
];

interface KeyboardShortcutsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function KeyboardShortcutsDialog({ open, onOpenChange }: KeyboardShortcutsProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Create Fuse instance for fuzzy search
  const fuse = useMemo(() => {
    const searchableItems = shortcuts.flatMap((section) =>
      section.items.map((item) => ({
        ...item,
        section: section.name,
      }))
    );

    return new Fuse(searchableItems, {
      keys: ['name'],
      threshold: 0.3,
      shouldSort: true,
    });
  }, []);

  const filteredShortcuts = useMemo(() => {
    if (!searchQuery) return shortcuts;

    const results = fuse.search(searchQuery);
    const groupedResults = results.reduce<Record<string, ShortcutItem[]>>((acc, { item }) => {
      if (!acc[item.section]) {
        acc[item.section] = [];
      }
      acc[item.section].push(item);
      return acc;
    }, {});

    return shortcuts
      .map((section) => ({
        ...section,
        items: groupedResults[section.name] || [],
      }))
      .filter((section) => section.items.length > 0);
  }, [searchQuery, fuse]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 p-0 w-[450px] h-[600px]">
        <DialogDescription className="sr-only">Keyboard Shortcuts</DialogDescription>
        <DialogHeader className="flex-none px-4 pb-2 pt-5">
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">Keyboard Shortcuts</DialogTitle>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search shortcuts"
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </DialogHeader>
        <div className="custom-scrollbar flex-1 overflow-y-auto px-4 pb-4">
          {filteredShortcuts.map((section, i) => (
            <div key={section.name} className={cn('py-4', i !== filteredShortcuts.length - 1 && 'border-b')}>
              <h3 className="text-sm font-medium text-foreground">{section.name}</h3>
              <div className="mt-2 space-y-2">
                {section.items.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item.name}</span>
                    <div className="flex items-center gap-1">
                      {item.shortcut.map((key, index) => (
                        <span key={index} className="flex items-center gap-1">
                          {key === 'then' ? (
                            <span className="text-xs text-muted-foreground">then</span>
                          ) : (
                            <kbd className="bg-muted text-muted-foreground min-w-5 flex h-5 items-center justify-center rounded px-1 text-xs">
                              {key}
                            </kbd>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
